/* eslint-disable no-irregular-whitespace */
import { ExclamationCircleFilled } from '@ant-design/icons';
import queryString from 'query-string';
import event from '~helpers/mixpanel';
import {
  Button,
  Col,
  Divider,
  Icon,
  message,
  Popover,
  Row,
  Timeline,
  Tooltip,
  Empty,
  Spin,
} from 'antd';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import classNames from 'classnames';
import Paragraph from 'antd/lib/typography/Paragraph';
import LeadgenStudentInfo from './leadgen-student-info';
import { NotesDialog } from '../AddNotesActionField';
import { useMutation, useQuery } from '@apollo/client';
import { DELETE_ORDER_NOTE, QUERY_LEAD_ORDER_DETAIL } from '../queries';
import { useParams } from 'react-router-dom';
import moment from 'moment';
import ProductInfo from './product-info';
import BookingStatus from 'common/components/booking-status';
import CommissionInfo from 'common/components/commission-info';
import { decodeBase64 } from 'common/helpers/decodeBase64';
import { pageType } from '~constants/pages';

function LeadgenDetails() {
  const { referenceId } = useParams();
  const { t } = useTranslation();

  const {
    data: tempBookData,
    loading: bookingDetailLoading,
    refetch,
  } = useQuery(QUERY_LEAD_ORDER_DETAIL, {
    variables: { referenceId },
    onCompleted: () => {},
    fetchPolicy: 'network-only',
    onError: error => {
      console.error(error);
    },
  });
  const bookingData = tempBookData?.viewer?.company?.orders?.edges
    ? tempBookData?.viewer?.company?.orders?.edges[0]?.node
    : null;

  const renderTooltipContainer = text => (
    <div className="longtail-booking-details__tooltip-container">
      <span className="longtail-booking-details__tooltip-container__text">{text}</span>
    </div>
  );
  const headerNavList = {
    'student-info': 'student',
    'product-info': 'product',
    'commission-info': 'commission-info',
    'note-info': 'note',
  };
  const [currentTab, setCurrentTab] = React.useState(0);
  const [openAddNotes, setOpenAddNotes] = React.useState(null);
  const [openDeleteModal, setOpenDeleteModal] = React.useState(-1);
  const [firstLoad, setFirstLoad] = React.useState(true);

  const handleNavClick = (target, idx) => {
    const targetElement = document.querySelector(`#${target}`);

    setCurrentTab(idx);
    if (targetElement) {
      window.scrollTo(0, targetElement.offsetTop - 73);
    }
  };

  const [deleteOrderNote, { loading: deleteOrderNoteLoading }] = useMutation(DELETE_ORDER_NOTE, {
    variables: { input: { id: 'noteId' } },
    onCompleted: () => {
      refetch && refetch();
      setOpenDeleteModal(-1);
    },
    onError: e => {
      message.error(e.toString());
      setOpenDeleteModal(-1);
    },
  });

  const combineName = (firstName, lastName) => {
    let name = '';
    if (firstName) {
      name += firstName;
    }
    if (firstName && lastName) {
      name += ` ${lastName}`;
    } else if (lastName) {
      name = lastName;
    } else {
      name = '-';
    }
    return name;
  };

  const handlePageViewedTracking = bookingData => {
    const { property, stage, bookingJourney, referenceId } = bookingData || {};
    const { name, slug, id } = property || {};
    const decodedId = decodeBase64(id)?.id;
    const query = queryString.parse(window.location.search);
    const from = query.from?.split('|') || [];
    const referrerPage = from[0] || null;
    const referrerPosition = from[1] || null;

    event('Viewed Booking Details Page', {
      'Entry Position': {
        'Referrer Page': referrerPage,
        'Referrer Position': referrerPosition,
      },
      'Property Name': name,
      'Property ID': decodedId,
      'Property Slug': slug,
      'Booking Stage': stage?.toLowerCase(),
      'Booking Journey': bookingJourney?.toLowerCase(),
      'Booking ID': referenceId,
      'Current Page Type': pageType.bookingDetailPage,
    });
  };

  useEffect(() => {
    if (bookingData && firstLoad) {
      handlePageViewedTracking(bookingData);
      setFirstLoad(false);
    }
  }, [bookingData, firstLoad]);

  return !bookingData || bookingDetailLoading ? (
    <Spin style={{ marginTop: '45%', marginLeft: '45%' }} />
  ) : (
    <div className="longtail-booking-details">
      <div className="longtail-booking-details__header-container">
        <div className="longtail-booking-details__crumb">
          <Link to="/booking/list">{t('landlord.header.menu_link.booking')}</Link>
          <span className="longtail-booking-details__slash">/</span>
          <Link to="/booking/list">{t('landlord.header.menu_link.booking.list')}</Link>
          <span className="longtail-booking-details__slash">/</span>
          <span className="longtail-booking-details__current">
            {t('landlord.header.menu_link.booking.details')}
          </span>
        </div>
        <Row style={{ paddingBottom: '24px' }}>
          <Col span={16}>
            <h3 className="longtail-booking-details__booking-number">
              {`${t('landlord.long_tail.booking_details.title.booking_number')} - ${referenceId}`}
            </h3>
            <div className="longtail-booking-details__student-info">
              <Row className="longtail-booking-details__info-row" gutter={50}>
                <Col span={16} className="longtail-booking-details__info-col">
                  <Tooltip
                    overlayClassName="longtail-booking-details__info-tooltip"
                    placement="topLeft"
                    trigger="hover"
                    overlay={renderTooltipContainer(
                      `Student Name:${combineName(
                        bookingData?.student?.passportFirstName,
                        bookingData?.student?.passportLastName,
                      )}`,
                    )}
                  >
                    <Paragraph ellipsis className="longtail-booking-details__info">
                      {`Student Name:${combineName(
                        bookingData?.student?.passportFirstName,
                        bookingData?.student?.passportLastName,
                      )}`}
                    </Paragraph>
                  </Tooltip>
                  <Tooltip
                    overlayClassName="longtail-booking-details__info-tooltip"
                    placement="topLeft"
                    trigger="hover"
                    overlay={renderTooltipContainer(
                      t('landlord.long_tail.booking_details.label.student_mobile', {
                        value: bookingData?.student?.phoneNumber,
                      }),
                    )}
                  >
                    <Paragraph ellipsis className="longtail-booking-details__info">
                      {t('landlord.long_tail.booking_details.label.student_mobile', {
                        value: bookingData?.student?.phoneNumber,
                      })}
                    </Paragraph>
                  </Tooltip>
                  <Tooltip
                    overlayClassName="longtail-booking-details__info-tooltip"
                    placement="topLeft"
                    trigger="hover"
                    overlay={renderTooltipContainer(
                      t('landlord.long_tail.booking_details.label.student_email', {
                        value: bookingData?.student?.emailAddress,
                      }),
                    )}
                  >
                    <Paragraph ellipsis className="longtail-booking-details__info">
                      {t('landlord.long_tail.booking_details.label.student_email', {
                        value: bookingData?.student?.emailAddress,
                      })}
                    </Paragraph>
                  </Tooltip>
                </Col>
                <Col
                  span={8}
                  className="longtail-booking-details__info-col"
                  style={{ width: 'auto', marginLeft: 0, paddingLeft: 0 }}
                >
                  <Tooltip
                    overlayClassName="longtail-booking-details__info-tooltip"
                    placement="topLeft"
                    trigger="hover"
                    overlay={renderTooltipContainer(
                      t('landlord.long_tail.booking_details.label.room') +
                        (bookingData.unitTypeCategory
                          ? t(
                              `landlord.long_tail.booking.unit_type_category.${bookingData.unitTypeCategory.toLowerCase()}`,
                            )
                          : '') +
                        (bookingData.unitTypeName ? `, ${bookingData.unitTypeName}` : '') +
                        (bookingData.propertyName ? `, ${bookingData.propertyName}` : ''),
                    )}
                  >
                    <Paragraph ellipsis className="longtail-booking-details__info">
                      {t('landlord.long_tail.booking_details.label.room') +
                        (bookingData.unitTypeCategory
                          ? t(
                              `landlord.long_tail.booking.unit_type_category.${bookingData.unitTypeCategory.toLowerCase()}`,
                            )
                          : '') +
                        (bookingData.unitTypeName ? `, ${bookingData.unitTypeName}` : '') +
                        (bookingData.propertyName ? `, ${bookingData.propertyName}` : '')}
                    </Paragraph>
                  </Tooltip>
                  <Tooltip
                    overlayClassName="longtail-booking-details__info-tooltip"
                    placement="topLeft"
                    trigger="hover"
                    overlay={renderTooltipContainer(
                      'Desired ' +
                        t('landlord.long_tail.booking_details.label.move_in_date', {
                          value: bookingData.moveInDate
                            ? moment(bookingData.moveInDate).format('DD/MM/YYYY')
                            : '-',
                        }),
                    )}
                  >
                    <Paragraph ellipsis className="longtail-booking-details__info">
                      {'Desired ' +
                        t('landlord.long_tail.booking_details.label.move_in_date', {
                          value: bookingData.moveInDate
                            ? moment(bookingData.moveInDate).format('DD/MM/YYYY')
                            : '-',
                        })}
                    </Paragraph>
                  </Tooltip>
                  <Tooltip
                    overlayClassName="longtail-booking-details__info-tooltip"
                    placement="topLeft"
                    trigger="hover"
                    overlay={renderTooltipContainer(
                      'Desired ' +
                        t('landlord.long_tail.booking_details.label.move_out_date', {
                          value: bookingData.moveOutDate
                            ? moment(bookingData.moveOutDate).format('DD/MM/YYYY')
                            : '-',
                        }),
                    )}
                  >
                    <Paragraph ellipsis className="longtail-booking-details__info">
                      {'Desired ' +
                        t('landlord.long_tail.booking_details.label.move_out_date', {
                          value: bookingData.moveOutDate
                            ? moment(bookingData.moveOutDate).format('DD/MM/YYYY')
                            : '-',
                        })}
                    </Paragraph>
                  </Tooltip>
                </Col>
              </Row>
            </div>
          </Col>
          <Col span={8}>
            <BookingStatus
              id={bookingData.id}
              stage={bookingData.stage}
              canUpdate={bookingData.canUpdateListOrderStage}
            />
          </Col>
        </Row>
      </div>
      <div className="longtail-booking-details__header-nav">
        <ul className="longtail-booking-details__nav-list">
          <For of={Object.keys(headerNavList)} each="item" index="index">
            <li
              key={item}
              className={classNames('longtail-booking-details__nav-item', {
                'longtail-booking-details__nav-item--active': currentTab === index,
              })}
              onClick={() => {
                handleNavClick(item, index);
              }}
              role="presentation"
            >
              <span className="longtail-booking-details__nav-label">
                {t(`landlord.long_tail.booking_details.label.${item.replace(/-/g, '_')}`)}
              </span>
            </li>
          </For>
        </ul>
      </div>

      <div className="longtail-booking-details__block" id="student-info">
        <label className="longtail-booking-details__block-title">
          {t('landlord.long_tail.booking_details.label.student_info')}
        </label>
        <LeadgenStudentInfo t={t} student={bookingData?.studentData} bookingData={bookingData} />
      </div>
      <div className="longtail-booking-details__block" id="product-info">
        <label className="longtail-booking-details__block-title">
          {t('landlord.long_tail.booking_details.label.product_info')}
        </label>
        <ProductInfo bookingData={bookingData} property={bookingData?.property} />
      </div>
      <div className="longtail-booking-details__block" id="commission-info">
        <label className="longtail-booking-details__block-title">
          {t('landlord.long_tail.booking_details.label.commission_info')}
        </label>
        <CommissionInfo commissionTriggerPoint={bookingData?.commissionTriggerPoint} />
      </div>
      <div className="longtail-booking-details__block" id="note-info">
        <label className="longtail-booking-details__block-title">{'Notes'}</label>
        {!bookingData?.orderNotes?.length ? (
          <div style={{ textAlign: 'center', marginTop: '34px' }}>
            <Empty description={'This booking does not have any notes yet'} />
            <Button
              style={{
                borderColor: '#5abfb2',
                color: '#5abfb2',
                width: 150,
                marginTop: 16,
                height: 32,
              }}
              onClick={() => {
                setOpenAddNotes({
                  model: 'ADD',
                });
              }}
            >
              <Icon type="plus" /> Add New Note
            </Button>
          </div>
        ) : (
          <>
            <div>
              <Button
                type="dashed"
                block
                style={{ borderColor: '#5abfb2', color: '#5abfb2' }}
                onClick={() => {
                  setOpenAddNotes({
                    model: 'ADD',
                  });
                }}
              >
                <Icon type="plus" /> Add
              </Button>
            </div>
            <div style={{ marginTop: '32px' }}>
              <Timeline>
                {bookingData?.orderNotes?.map((item, idx) => (
                  <Timeline.Item
                    key={idx}
                    color="gray"
                    dot={
                      <div
                        style={{
                          width: '8px',
                          height: '8px',
                          backgroundColor: '#BFBFBF',
                          borderRadius: '50%',
                        }}
                      />
                    }
                    style={{
                      fontSize: '14px',
                      lineHeight: '22px',
                      fontWeight: 400,
                      color: 'rgba(0, 0, 0, 0.45)',
                    }}
                  >
                    <div style={{ display: 'flex' }}>
                      <div>
                        <div style={{ color: 'rgba(0, 0, 0, 0.85)' }}>{item.note}</div>
                        <div
                          style={{
                            display: 'flex',
                            flexDirection: 'row',
                            alignItems: 'center',
                          }}
                        >
                          <div> {moment(item.updatedAt).format('MMM DD,YYYY')}</div>
                          <Divider type="vertical" />
                          <div>{item.updatedByName} </div>
                        </div>
                      </div>
                      <div style={{ marginLeft: 'auto' }}>
                        <Button
                          type="link"
                          onClick={() => {
                            setOpenAddNotes({
                              model: 'EDIT',
                              noteId: item.id,
                              note: item.note,
                            });
                          }}
                        >
                          Edit
                        </Button>
                        <Divider type="vertical" />
                        <Popover
                          placement="topRight"
                          trigger="click"
                          visible={idx === openDeleteModal}
                          onVisibleChange={v => {
                            if (v) {
                              setOpenDeleteModal(idx);
                            } else {
                              setOpenDeleteModal(-1);
                            }
                          }}
                          content={
                            <div className="longtail-booking-details__popover">
                              <div className="wrapper">
                                <ExclamationCircleFilled
                                  className="icon"
                                  style={{ color: '#F9A600' }}
                                />

                                <div className="txt">Are you sure delete this note?</div>
                              </div>
                              <div className="btn_group">
                                <Button
                                  type="ghost"
                                  onClick={() => {
                                    setOpenDeleteModal(-1);
                                  }}
                                >
                                  Cancel
                                </Button>
                                <Button
                                  loading={deleteOrderNoteLoading}
                                  type="danger"
                                  style={{ backgroundColor: '#FF4D4F' }}
                                  onClick={() => {
                                    deleteOrderNote({
                                      variables: {
                                        input: {
                                          id: item.id,
                                        },
                                      },
                                    });
                                  }}
                                >
                                  Delete
                                </Button>
                              </div>
                            </div>
                          }
                        >
                          <Button
                            type="link"
                            style={{ color: 'red' }}
                            onClick={() => {
                              setOpenDeleteModal(idx);
                            }}
                          >
                            Delete
                          </Button>
                        </Popover>
                      </div>
                    </div>
                  </Timeline.Item>
                ))}
              </Timeline>
            </div>
          </>
        )}
      </div>
      <NotesDialog
        open={openAddNotes}
        // eslint-disable-next-line no-unused-vars
        setOpen={_ => {
          setOpenAddNotes(null);
        }}
        model={openAddNotes?.model}
        noteId={openAddNotes?.noteId}
        note={openAddNotes?.note}
        bookingId={bookingData?.id}
        refetchData={refetch}
      />
    </div>
  );
}

export default LeadgenDetails;
